import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {getFormMessages, isAssignedTickets, isWixFormsSupported} from '@wix/wix-events-commons-statics'
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor'
import React, {useEffect, useRef} from 'react'
import {isTemplate} from '../../../../../commons/selectors/instance'
import {CHECKOUT_STEPS, FormStep} from '../../constants/constants'
import {useAppActions, useAppSelector} from '../../hooks/runtime'
import {
  getBuyerDetails,
  getBuyerDetailsOld,
  getCurrentStep,
  isAllTicketDetailsValid,
  isStepCompleted,
  isStepVisible,
  isValidPaymentAdded,
} from '../../selectors/checkout'
import {isCheckoutVisibilityEnabled} from '../../selectors/checkout-visibility'
import {getEvent} from '../../selectors/event'
import {isWixFormsEnabled} from '../../selectors/events-settings'
import {getGiftCardPaymentDetails} from '../../selectors/invoice'
import {isEventPreview} from '../../selectors/navigation'
import {getInvoice, getPlacedOrder} from '../../selectors/placed-order'
import {hasAgreedWithPolicies, hasPolicies} from '../../selectors/policies'
import {
  CheckoutPaymentPath,
  CheckoutStage,
  CheckoutStageOutcome,
  getCheckoutFailureContext,
  getPaymentFailureContext,
} from '../../services/checkout-visibility'
import {BuyerDetails} from '../buyer-details'
import {StatusScreensEntry} from '../chunks/status-screens-entry'
import {Policies} from '../policies'
import {CheckoutProps} from './interfaces'
import {Payment} from './payment'
import {PaymentWidget} from './payment/payment-widget'
import {TicketsDetails} from './tickets-details'
import {TicketsDetailsV2} from './tickets-details-v2'
import {FormRef} from './tickets-details-v2/interfaces'

export const Checkout = ({isMobile, children}: CheckoutProps) => {
  const {t} = useTranslation()
  const {experiments} = useExperiments()
  const {
    editStep,
    setExpandedTicketIndex,
    submitCheckoutStep,
    submitCheckoutStepOld,
    setValidPaymentAdded,
    paymentMethodSelected,
    handleNextStep,
    placeOrderButtonClicked,
    reportCheckoutVisibilityStage,
    reportCheckoutVisibilityStageOnce,
    setCheckoutVisibilityPaymentPath,
  } = useAppActions()
  const payment = useRef<PaymentWidget>(null)
  const formRefs = useRef<FormRef[]>([])

  const placedOrder = useAppSelector(getPlacedOrder)
  const invoice = useAppSelector(getInvoice)
  const buyerDetails = useAppSelector(getBuyerDetails)
  const buyerDetailsOld = useAppSelector(getBuyerDetailsOld)
  const currentStep = useAppSelector(getCurrentStep)
  const allTicketDetailsValid = useAppSelector(isAllTicketDetailsValid)
  const validPaymentAdded = useAppSelector(isValidPaymentAdded)
  const visibilityEnabled = useAppSelector(isCheckoutVisibilityEnabled)
  const giftCardPaymentDetails = useAppSelector(getGiftCardPaymentDetails)
  const agreedWithPolicies = useAppSelector(hasAgreedWithPolicies)
  const withPolicies = useAppSelector(hasPolicies)
  const template = useAppSelector(isTemplate)
  const eventPreview = useAppSelector(isEventPreview)
  const event = useAppSelector(getEvent)
  const wixFormsEnabled = useAppSelector(state => isWixFormsEnabled(state, experiments))
  const assignedTicketsEnabled = isAssignedTickets(event)
  const messages = getFormMessages(event)

  const useWixForms = isWixFormsSupported(event) && wixFormsEnabled

  useEffect(() => {
    if (placedOrder?.orderNumber) {
      const url = `${window.location.href}&orderNumber=${placedOrder.orderNumber}`

      window.history.replaceState(window.history.state, null, url)
    }
  }, [placedOrder?.orderNumber])

  const getSteps = () =>
    CHECKOUT_STEPS.filter(step =>
      isStepVisible({
        step,
        assignedTicketsEnabled,
        invoice,
        giftCardPaymentDetails,
        eventPreview,
        hasPolicies: withPolicies,
        isTemplate: template,
      }),
    ).map(step => {
      const stepTitle = t(`mobile.checkout.step.${step}`)
      const buyerDetailsTitle = messages?.checkout?.title ?? stepTitle
      const title = step === FormStep.BuyerDetails ? buyerDetailsTitle : stepTitle

      return {
        key: step,
        title,
        completed: isStepCompleted({
          step,
          placedOrder,
          assignedTicketsEnabled,
          buyerDetails,
          buyerDetailsOld,
          validPaymentAdded,
          agreedWithPolicies,
          allTicketDetailsValid,
          hasPolicies: withPolicies,
        }),
        renderStep: renderStep(step),
        onEdit: handleStepEdit,
      }
    })

  const handleStepEdit = async (step: FormStep) => {
    editStep({step, validPaymentAdded})

    if (step === FormStep.TicketsDetails) {
      setExpandedTicketIndex(0)
    } else if (step === FormStep.Payment) {
      payment.current.expand()
    } else {
      setExpandedTicketIndex(-1)
    }
  }

  const renderStep = (step: FormStep) => {
    const onSubmit = (data: any) => {
      if (currentStep === FormStep.Payment) {
        return handlePaymentStep()
      } else {
        StatusScreensEntry.preload()
        useWixForms ? submitCheckoutStep(data) : submitCheckoutStepOld(data)
      }
    }

    switch (step) {
      case FormStep.BuyerDetails:
        return (editing: boolean) => <BuyerDetails editing={editing} onSubmit={onSubmit} />
      case FormStep.Policies:
        return (editing: boolean) => <Policies editing={editing} onSubmit={onSubmit} />
      case FormStep.TicketsDetails:
        return (editing: boolean) =>
          useWixForms ? (
            <TicketsDetailsV2 editing={editing} formRefs={formRefs} onSubmit={onSubmit} />
          ) : (
            <TicketsDetails editing={editing} onSubmit={onSubmit} />
          )
      case FormStep.Payment:
        return (editing: boolean) => <Payment paymentRef={payment} editing={editing} onSubmit={onSubmit} />
      default:
        return () => null
    }
  }

  const handlePaymentStep = async () => {
    const validPaymentAdded = await payment.current.validate()

    if (validPaymentAdded) {
      if (visibilityEnabled) {
        setCheckoutVisibilityPaymentPath(getPaymentPath(payment.current.props.currentPaymentMethodId))
        reportCheckoutVisibilityStageOnce(CheckoutStage.PaymentUi, CheckoutStageOutcome.Success)
      }
      paymentMethodSelected(payment.current.props.currentPaymentMethodId)
      await pay()
    } else {
      setValidPaymentAdded(validPaymentAdded)
      if (visibilityEnabled) {
        reportCheckoutVisibilityStage(CheckoutStage.PaymentSubmission, CheckoutStageOutcome.BusinessFailure, {
          reason: 'validation_failed',
        })
      }
    }

    handleNextStep()
  }

  const pay = async () => {
    if (payment.current) {
      if (visibilityEnabled) {
        reportCheckoutVisibilityStage(CheckoutStage.PaymentSubmission, CheckoutStageOutcome.Started)
        reportCheckoutVisibilityStage(CheckoutStage.PaymentAuthorization, CheckoutStageOutcome.Started)
      }

      try {
        const status = await payment.current.pay()
        if (visibilityEnabled) {
          reportCheckoutVisibilityStageOnce(CheckoutStage.PaymentSubmission, CheckoutStageOutcome.Success)
        }

        // @ts-expect-error
        if (status.errorCode) {
          if (visibilityEnabled) {
            reportCheckoutVisibilityStage(
              CheckoutStage.PaymentAuthorization,
              CheckoutStageOutcome.BusinessFailure,
              getPaymentFailureContext(status),
            )
          }
          setValidPaymentAdded(false)
          await handleStepEdit(FormStep.Payment)
          isMobile && focusElement({selector: hookToAttributeSelector('pw-payment-error')})
        } else {
          if (visibilityEnabled) {
            const offline = payment.current.props.currentPaymentMethodId === 'offline'
            reportCheckoutVisibilityStageOnce(
              CheckoutStage.PaymentAuthorization,
              offline ? CheckoutStageOutcome.Pending : CheckoutStageOutcome.Success,
              {paymentStatus: offline ? 'Offline' : 'OK'},
            )
          }
          placeOrderButtonClicked()
        }

        return status
      } catch (error) {
        if (visibilityEnabled) {
          reportCheckoutVisibilityStage(
            CheckoutStage.PaymentSubmission,
            CheckoutStageOutcome.TechnicalFailure,
            getCheckoutFailureContext(error),
          )
        }
        throw error
      }
    }
  }

  return children(getSteps(), currentStep, pay)
}

const getPaymentPath = (paymentMethodId: string) =>
  paymentMethodId === 'offline'
    ? CheckoutPaymentPath.Offline
    : paymentMethodId
      ? CheckoutPaymentPath.Online
      : CheckoutPaymentPath.Unselected
